'use client';

import React, { useCallback, useMemo } from 'react';
import clsx from 'clsx';
import { useFloating, offset, flip, shift, autoUpdate } from '@floating-ui/react-dom';
import { getCurrentUrl } from '@pixum/auth-core-ui';
import Portal from '@pixum/portal';
import HotspotContent from './HotspotContent';
import './styles.css';
import type { HotspotItemProps } from './types';
import { JENTIS_PENDING_IDENTIFIER, redirectToWithPageReload, SessionStorage } from 'utils';
import { useDeviceInfo } from 'hooks';

const HOTSPOT_TRACKING_CATEGORY = 'hot_spot';

/**
 * Renders a single hotspot item on the image and displays detailed content
 * when hovered or clicked, depending on screen size.
 *
 * @param props - The component props.
 * @param props.hotspotContent - Content data to display in the tooltip.
 * @param props.isOpenedBydefault - Whether the item is open initially.
 * @param props.indexOpen - The index of the currently open hotspot.
 * @param  props.setIndexOpen - Function to update the open index.
 * @param {(timeout: NodeJS.Timeout) => void} props.setDelay - Function to store a delayed close timeout.
 * @param props.delay - Timeout ID for delayed closing.
 * @param props.index - Current item's index in the list.
 * @param props.style - Inline style for positioning the hotspot.
 * @returns The rendered hotspot item.
 */
export function HotspotItem(props: HotspotItemProps) {
  const { hotspotContent, portalTarget, isOpenedBydefault, indexOpen, setIndexOpen, setDelay, delay, index, style, constants } =
    props;
  const { refs, floatingStyles } = useFloating({
    middleware: [offset(10), shift({ padding: 10 }), flip()],
    whileElementsMounted: autoUpdate,
  });
  const { isMobile } = useDeviceInfo();
  const currentUrl = getCurrentUrl();
  const redirectUrl = hotspotContent.link;

  const isOpen = indexOpen !== null ? index === indexOpen : isOpenedBydefault;

  const hotspotItemClasses = clsx('hotspot-item', {
    'hotspot-item--active': isOpen,
    pulse: !isOpen,
  });

  const handleMouseEnter = useCallback(() => {
    if (!isMobile) {
      if (delay) clearTimeout(delay);
      setIndexOpen(index);
    }
  }, [isMobile, delay, index, setIndexOpen]);

  const handleMouseLeave = useCallback(() => {
    if (!isMobile) {
      const timeout = setTimeout(() => setIndexOpen(-1), 800);
      setDelay(timeout);
    }
  }, [isMobile, setIndexOpen, setDelay]);

  const handleClick = useCallback(() => {
    if (!isMobile) {
      const trackingData = {
        module: HOTSPOT_TRACKING_CATEGORY,
        target: currentUrl,
      };

      SessionStorage.setItem(JENTIS_PENDING_IDENTIFIER, JSON.stringify(trackingData));

      if (redirectUrl) {
        redirectToWithPageReload(redirectUrl);
      }
    } else {
      if (isOpen) {
        setIndexOpen(-1);
      } else {
        setIndexOpen(index);
      }
    }
  }, [isMobile, isOpen, index, setIndexOpen, redirectUrl, currentUrl]);

  const elementStyles = useMemo(
    () => ({
      ...style,
      animationDelay: `${index}s`,
    }),
    [style, index],
  );

  return (
    <li
      aria-describedby={`hotspot-item-${index + 1}`}
      className={hotspotItemClasses}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      ref={refs.setReference}
      style={elementStyles}
    >
      {portalTarget && (
        <Portal container={portalTarget}>
          <HotspotContent
            {...hotspotContent}
            floatingStyles={floatingStyles}
            index={index}
            isVisible={isOpen}
            price={hotspotContent.price}
            setFloating={refs.setFloating}
            constants={constants}
          />
        </Portal>
      )}
    </li>
  );
}

export default React.memo(HotspotItem);
